import { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import axios from "axios";
import { Modal } from "antd";
import http from "../../../api/http";
import { blockWrites } from "../../../api/writeGuards";
import navStyles from "../../navV3/NavShellV3.module.css";
import { openSupportTelegram } from "../../navV3/support";
import styles from "./MaintenanceWatcher.module.css";
import {
  BACK_CLOSE_MS,
  POLL_MS,
  REQUEST_TIMEOUT_MS,
  RETRY_MS,
  bundleVersion,
  initialWatchState,
  isBlocking,
  isOutdated,
  isShown,
  nextWatchState,
} from "./maintenanceState";

export { POLL_MS };

// Страницы показывают data.message как текст ошибки. Новый объект на каждый
// запрос: обработчики ошибок иногда дописывают в него поля.
const maintenanceError = () => ({
  isMaintenance: true,
  response: {
    status: 503,
    data: {
      code: "MAINTENANCE",
      message: "Сейчас идёт обновление, сохранить не получилось. Попробуйте через несколько минут.",
    },
  },
});

// Тексты проверены «тестом на бабушку»: без слов «деплой», «сервер», «JS/CSS».
const TEXTS = {
  maintenance: {
    tag: "Обновление",
    title: "Обновляем Упрости",
    body: "Это займёт несколько минут. Пока идёт обновление, сохранить изменения нельзя. Всё, что вы уже сохранили, на месте.",
    hint: "Ничего нажимать не нужно: окно закроется само, когда всё будет готово. То, что вы набрали на странице, останется.",
  },
  unreachable: {
    tag: "Нет связи",
    title: "Упрости ненадолго недоступен",
    body: "Скорее всего, мы ставим обновление. Пока связи нет, сохранить изменения нельзя. Всё, что вы уже сохранили, на месте.",
    hint: "Ничего нажимать не нужно: окно закроется само, как только связь вернётся. То, что вы набрали на странице, останется.",
  },
  back: {
    tag: "Готово",
    title: "Упрости снова работает",
    body: "Можно продолжать: всё, что вы набрали, на месте. Если что-то не сохранилось, нажмите «Сохранить» ещё раз.",
  },
};

const cx = (...names) => names.filter(Boolean).join(" ");

// Сборка, которая открыта у человека сейчас.
const OWN_VERSION = bundleVersion(
  typeof document !== "undefined" &&
    document.querySelector('script[src*="/static/js/main."]')?.getAttribute("src")
);

export default function MaintenanceWatcher() {
  const isDark = useSelector((state) => Boolean(state?.ToggleDarkModeReducer?.chekced));

  const [watch, setWatch] = useState(initialWatchState);
  const [serverVersion, setServerVersion] = useState(null);
  const [dismissedVersion, setDismissedVersion] = useState(null);
  const watchRef = useRef(initialWatchState);

  const apply = (event) => {
    const next = nextWatchState(watchRef.current, event, Date.now());
    watchRef.current = next;
    setWatch(next);
    return next;
  };

  // ---------------------------
  // 1) Опрос статуса техработ
  // ---------------------------
  useEffect(() => {
    let timer = null;
    let stopped = false;

    const schedule = (ms) => {
      clearTimeout(timer);
      if (!stopped) timer = setTimeout(check, ms);
    };

    const check = async () => {
      // Скрытую вкладку не опрашиваем: проверим, когда её откроют.
      if (document.hidden) return schedule(POLL_MS);

      let next;
      try {
        const res = await axios.get("/maintenance/status", {
          timeout: REQUEST_TIMEOUT_MS,
          headers: { "Cache-Control": "no-store", Pragma: "no-cache" },
        });
        if (stopped) return;
        // Сайт отвечает, но база недоступна: сохранить всё равно ничего нельзя,
        // поэтому считаем это такой же ошибкой, как молчание сервера.
        next =
          res?.data?.db === false
            ? apply({ type: "error", offline: false })
            : apply({ type: "status", on: Boolean(res?.data?.on) });
        if (res?.data?.version) setServerVersion(res.data.version);
      } catch (e) {
        if (stopped) return;
        next = apply({ type: "error", offline: navigator.onLine === false });
      }
      schedule(next.fails > 0 ? RETRY_MS : POLL_MS);
    };

    const resume = () => {
      if (document.hidden) return;
      apply({ type: "resume" });
      schedule(0);
    };

    check();
    document.addEventListener("visibilitychange", resume);
    window.addEventListener("online", resume);

    return () => {
      stopped = true;
      clearTimeout(timer);
      document.removeEventListener("visibilitychange", resume);
      window.removeEventListener("online", resume);
    };
  }, []);

  // ---------------------------
  // 2) Блокировка изменений, пока окно открыто: и обычный axios, и api/http.js
  // ---------------------------
  const blocking = isBlocking(watch.phase);
  useEffect(() => {
    if (!blocking) return undefined;
    return blockWrites([axios, http], maintenanceError);
  }, [blocking]);

  // ---------------------------
  // 3) Снова работает: окно закрывается само, страницу не перезагружаем,
  //    чтобы не стереть набранное. Новую версию предлагает плашка ниже.
  // ---------------------------
  const isBack = watch.phase === "back";
  useEffect(() => {
    if (!isBack) return undefined;
    const close = setTimeout(() => apply({ type: "close" }), BACK_CLOSE_MS);
    return () => clearTimeout(close);
  }, [isBack]);

  const text = TEXTS[watch.phase] || TEXTS.maintenance;
  const outdated = isOutdated(OWN_VERSION, serverVersion, dismissedVersion);
  const themeClass = cx(navStyles.tokens, isDark && navStyles.portalDark);

  return (
    <>
      <Modal
        open={isShown(watch.phase)}
        centered
        width={460}
        closable={false}
        maskClosable={false}
        keyboard={false}
        zIndex={5000000}
        footer={null}
        rootClassName={cx(themeClass, styles.root)}
      >
        <div className={styles.card}>
          <span className={cx(styles.tag, isBack && styles.tagDone)}>{text.tag}</span>

          <h2 className={styles.title}>{text.title}</h2>
          <p className={styles.body}>{text.body}</p>

          {isBack ? (
            <button type="button" className={styles.button} onClick={() => apply({ type: "close" })}>
              Продолжить
            </button>
          ) : (
            <>
              <div className={cx(styles.bar, styles.barRunning)} aria-hidden="true" />
              <p className={styles.hint}>{text.hint}</p>
              <p className={styles.help}>
                Окно не пропадает дольше 15 минут?{" "}
                <button type="button" className={styles.link} onClick={openSupportTelegram}>
                  Напишите нам
                </button>
              </p>
            </>
          )}
        </div>
      </Modal>

      {outdated && !isShown(watch.phase) && (
        <div className={cx(themeClass, styles.update)} role="status">
          <div className={styles.updateText}>
            <strong className={styles.updateTitle}>Вышла новая версия Упрости</strong>
            <span>Сохраните то, что делаете, и обновите страницу.</span>
          </div>
          <div className={styles.updateActions}>
            <button type="button" className={styles.updateButton} onClick={() => window.location.reload()}>
              Обновить
            </button>
            <button type="button" className={styles.link} onClick={() => setDismissedVersion(serverVersion)}>
              Позже
            </button>
          </div>
        </div>
      )}
    </>
  );
}
